import { Box, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@material-ui/core";
import { Assignment, AssignmentTurnedIn, Event, Notes } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { isOverdueTask, orderListingActivity } from "utils/listings/listingActivity";
import type { ListingActivityItem } from "utils/listings/listingActivity";
import { activityText, activityWho, formatActivityWhen } from "./activityText";

type Props = {
  items: ListingActivityItem[];
  /** Teammates' names by lowercased email. */
  names: ReadonlyMap<string, string>;
  now: Date;
  /** Cap the rows (the board's popover); the listing window shows all. */
  maxItems?: number;
};

function ItemIcon({ item, overdue }: { item: ListingActivityItem; overdue: boolean }) {
  if (item.kind === "note") return <Notes sx={{ fontSize: 18, color: "warning.main" }} />;
  if (item.kind === "meeting") return <Event sx={{ fontSize: 18, color: item.isAhead ? "primary.main" : "text.disabled" }} />;
  return item.isDone ? (
    <AssignmentTurnedIn sx={{ fontSize: 18, color: "success.main" }} />
  ) : (
    // Red like the board's icon, which this task turned red.
    <Assignment sx={{ fontSize: 18, color: overdue ? "error.main" : "info.main" }} />
  );
}

/**
 * A listing's activity as one list: what is still ahead (meetings to come, tasks to do — an overdue one
 * first), then what already happened, newest first. Each row says what, who and when. Presentational:
 * the items come from `utils/listings/listingActivity`.
 */
export function ListingActivityList({ items, names, now, maxItems }: Props) {
  const { t, i18n } = useTranslation();
  const { ahead, past } = orderListingActivity(items);
  const aheadShown = maxItems ? ahead.slice(0, maxItems) : ahead;
  const pastShown = maxItems ? past.slice(0, Math.max(0, maxItems - aheadShown.length)) : past;
  const hidden = items.length - aheadShown.length - pastShown.length;

  const row = (item: ListingActivityItem) => {
    const when = formatActivityWhen(item, now, t, i18n.language);
    const overdue = isOverdueTask(item, now);
    return (
      <ListItem key={item.key} disableGutters alignItems="flex-start" sx={{ py: 0.25 }}>
        <ListItemAvatar sx={{ minWidth: 30, mt: 0.5 }}>
          <ItemIcon item={item} overdue={overdue} />
        </ListItemAvatar>
        <ListItemText
          sx={{ my: 0.25 }}
          primary={
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                ...(item.isDone ? { textDecoration: "line-through", color: "text.secondary" } : {})
              }}
            >
              {activityText(item, t)}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color={overdue ? "error.main" : "text.secondary"}>
              {t(`listingActivity.kind.${item.kind}`)} · {activityWho(item, names, t)} · {when}
              {overdue && ` · ${t("listingActivity.overdue")}`}
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Box>
      {aheadShown.length > 0 && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 2 }}>
            {t("listingActivity.ahead")}
          </Typography>
          <List dense disablePadding>
            {aheadShown.map(row)}
          </List>
        </>
      )}
      {pastShown.length > 0 && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 2, mt: aheadShown.length ? 1 : 0 }}>
            {t("listingActivity.past")}
          </Typography>
          <List dense disablePadding>
            {pastShown.map(row)}
          </List>
        </>
      )}
      {hidden > 0 && (
        <Typography variant="caption" color="text.secondary">
          {hidden === 1 ? t("listingActivity.moreOne") : t("listingActivity.moreOther", { count: hidden })}
        </Typography>
      )}
    </Box>
  );
}
